import { initHotelSingleSlider } from "./slider";

/**
 * Fetches hotel data via REST API and replaces skeleton placeholders
 * with real rendered HTML.
 */
export function initHotelSingleLoader() {
    const container = document.querySelector("[data-dopline-hotel-loader]");
    if (!container) return;

    const slug = container.dataset.slug || "";
    const restUrl = container.dataset.restUrl || "";
    const restNonce = container.dataset.restNonce || "";

    if (!slug || !restUrl) return;

    let searchParams = {};
    try {
        searchParams = JSON.parse(container.dataset.searchParams || "{}");
    } catch {
        // ignore
    }

    const params = new URLSearchParams();
    params.set("slug", slug);
    if (searchParams.checkin) params.set("checkin", searchParams.checkin);
    if (searchParams.checkout) params.set("checkout", searchParams.checkout);
    if (searchParams.adults) params.set("adults", String(searchParams.adults));
    if (searchParams.children) params.set("children", String(searchParams.children));
    if (searchParams.children_ages && searchParams.children_ages.length > 0) {
        params.set("children_ages", searchParams.children_ages.join(","));
    }
    if (searchParams.hotel_hs) params.set("hotel_hs", searchParams.hotel_hs);
    if (searchParams.search_hs) params.set("search_hs", searchParams.search_hs);

    const url = restUrl + "?" + params.toString();

    fetch(url, {
        headers: {
            "X-WP-Nonce": restNonce,
        },
    })
        .then((res) => {
            if (!res.ok) {
                return res.text().then((text) => {
                    let parsed;
                    try { parsed = JSON.parse(text); } catch { /* HTML */ }
                    throw new Error(parsed?.message || `HTTP ${res.status}`);
                });
            }
            return res.text().then((text) => {
                try {
                    return JSON.parse(text);
                } catch {
                    throw new Error("Сервер вернул некорректный ответ");
                }
            });
        })
        .then((json) => {
            if (!json.success || !json.data || !json.data.html) {
                throw new Error(json.message || "Unknown error");
            }

            hydrateSection(container, json.data.html);

            // Update page title (fallback for cold-cache server-side renders)
            if (json.data.title) {
                const siteName = container.dataset.siteTitle || "";
                document.title = siteName
                    ? json.data.title + " – " + siteName
                    : json.data.title;
            }

            // Phase 2: fetch rooms separately when they were deferred server-side.
            // This avoids a long-running PHP polling loop that would time out Apache.
            if (json.data.rooms_pending) {
                loadRoomsAsync(container, searchParams, restUrl, restNonce, slug);
            }
        })
        .catch((err) => {
            showError(container, err.message);
        });
}

/**
 * Section key → data-hotel-section attribute value mapping.
 */
const SECTION_MAP = {
    search_wrap: "search-wrap",
    hero: "hero",
    gallery: "gallery",
    details: "details",
    map: "map",
    description: "description",
    rooms: "rooms",
    policy: "policy",
    facilities: "facilities",
};

function hydrateSection(container, htmlMap) {
    for (const [key, attr] of Object.entries(SECTION_MAP)) {
        const html = htmlMap[key];
        if (html === undefined) continue;

        const target = container.querySelector(`[data-hotel-section="${attr}"]`);
        if (!target) continue;

        if (!html || html.trim() === "") {
            // Section has no content (e.g. no description) — remove placeholder
            target.remove();
            continue;
        }

        // Create a wrapper, insert HTML, then replace the target
        const wrapper = document.createElement("div");
        wrapper.innerHTML = html;

        // Replace the skeleton element with the real content.
        // Template parts return a single root element, so unwrap if possible.
        const fragment = document.createDocumentFragment();
        while (wrapper.firstChild) {
            fragment.appendChild(wrapper.firstChild);
        }
        target.replaceWith(fragment);
    }

    // Re-initialize JS that depends on the new DOM
    reinitScripts();
}

function reinitScripts() {
    // Re-init Swiper sliders (gallery + room sliders)
    initHotelSingleSlider();

    // Re-init Leaflet map
    initMapFromDOM();

    // Room filter inline <script> tags are not executed by innerHTML.
    // Execute them manually.
    executeInlineScripts();

    // Re-init hotel search forms injected via REST (datepicker, autocomplete,
    // filters, guests picker). This fixes a race condition where
    // initHotelSearchScripts() ran before the REST response arrived, leaving
    // the search bar without flatpickr — which caused a native browser
    // validation error ("The string did not match the expected pattern") on
    // mobile when the form was submitted with a pre-filled date in d.m.Y format.
    document.querySelectorAll("[data-dopline-search]").forEach((root) => {
        window.DoplineHS?.autocomplete?.initAutocomplete(root);
        window.DoplineHS?.filters?.initFilters(root);
        window.DoplineHS?.filters?.initGuestsPicker(root);
        window.DoplineHS?.datepicker?.initDatepicker(root);
    });
    window.DoplineHS?.filters?.initSearchSync();
}

/**
 * Initialize Leaflet map from data attributes (mirrors map.js logic).
 */
function initMapFromDOM() {
    if (typeof window.L === "undefined") return;

    const mapWrap = document.querySelector("[data-dopline-single-map]");
    if (!mapWrap) return;

    // Prevent double-init (map.js may also run)
    if (mapWrap.querySelector(".leaflet-container")) return;

    const lat = parseFloat(mapWrap.dataset.lat);
    const lng = parseFloat(mapWrap.dataset.lng);
    if (isNaN(lat) || isNaN(lng)) return;

    const title = mapWrap.dataset.title || "";
    const address = mapWrap.dataset.address || "";

    const canvas = mapWrap.querySelector(".dopline-hotel-single__map-canvas");
    if (!canvas) return;

    const map = window.L.map(canvas, {
        zoomControl: true,
        scrollWheelZoom: false,
    });

    window.L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution: "&copy; OpenStreetMap contributors",
    }).addTo(map);

    const icon = window.L.divIcon({
        html: "<span>" + (title || "Отель") + "</span>",
        className: "dopline-map-price dopline-map-hotel-single-pin",
        iconSize: [180, 32],
        iconAnchor: [90, 16],
    });

    const popup = "<strong>" + title + "</strong>" + (address ? "<br>" + address : "");

    window.L.marker([lat, lng], { icon })
        .addTo(map)
        .bindPopup(popup)
        .openPopup();

    map.setView([lat, lng], 15);

    // Leaflet needs a resize nudge after being placed in newly-visible DOM
    setTimeout(() => map.invalidateSize(), 200);
}

/**
 * Execute inline <script> tags that were inserted via innerHTML
 * (the browser ignores them by default).
 */
function executeInlineScripts() {
    const container = document.querySelector("[data-dopline-hotel-loader]");
    if (!container) return;

    container.querySelectorAll("script").forEach((old) => {
        const s = document.createElement("script");
        // Copy attributes
        for (const attr of old.attributes) {
            s.setAttribute(attr.name, attr.value);
        }
        s.textContent = old.textContent;
        old.replaceWith(s);
    });
}

function showError(container, message) {
    // Remove all skeleton sections
    container.querySelectorAll("[data-hotel-section]").forEach((el) => el.remove());

    const div = document.createElement("div");
    div.className = "dl-card dopline-empty";
    div.textContent = message || "Не удалось загрузить данные отеля.";
    container.appendChild(div);
}

/**
 * Polls /hotel-single/rooms until rooms appear or max attempts are exhausted.
 * Each PHP request makes a single call to the availability API (~2–5 s),
 * so we never risk an Apache-level timeout from a long server-side polling loop.
 */
function loadRoomsAsync(container, searchParams, restUrl, restNonce, slug) {
    const roomsUrl = restUrl + "/rooms";

    const params = new URLSearchParams();
    params.set("slug", slug);
    if (searchParams.checkin) params.set("checkin", searchParams.checkin);
    if (searchParams.checkout) params.set("checkout", searchParams.checkout);
    if (searchParams.adults) params.set("adults", String(searchParams.adults));
    if (searchParams.children) params.set("children", String(searchParams.children));
    if (searchParams.children_ages && searchParams.children_ages.length > 0) {
        params.set("children_ages", searchParams.children_ages.join(","));
    }
    if (searchParams.hotel_hs) params.set("hotel_hs", searchParams.hotel_hs);
    if (searchParams.search_hs) params.set("search_hs", searchParams.search_hs);

    const url = roomsUrl + "?" + params.toString();

    let attempt = 0;
    const MAX_ATTEMPTS = 8;
    const RETRY_DELAY_MS = 2500;

    function fetchRooms() {
        attempt++;

        fetch(url, { headers: { "X-WP-Nonce": restNonce } })
            .then((res) => {
                if (!res.ok) {
                    return res.text().then(() => {
                        throw new Error(`HTTP ${res.status}`);
                    });
                }
                return res.json();
            })
            .then((json) => {
                if (!json.success) throw new Error(json.message || "Unknown error");

                if (json.data.has_rooms || attempt >= MAX_ATTEMPTS) {
                    // Rooms found (or give up) — replace the rooms skeleton
                    hydrateRooms(container, json.data.html);
                } else {
                    // API is still warming up — retry after a short delay
                    setTimeout(fetchRooms, RETRY_DELAY_MS);
                }
            })
            .catch((err) => {
                if (attempt < MAX_ATTEMPTS) {
                    setTimeout(fetchRooms, RETRY_DELAY_MS);
                } else {
                    const target = container.querySelector('[data-hotel-section="rooms"]');
                    if (target) {
                        const div = document.createElement("div");
                        div.className = "dl-card dopline-hotel-single__section";
                        div.innerHTML = '<p class="dopline-rooms__no-dates">Не удалось загрузить доступные номера. Попробуйте обновить страницу.</p>';
                        target.replaceWith(div);
                    }
                }
            });
    }

    // Start first attempt after 1 s so the main content has time to paint
    setTimeout(fetchRooms, 1000);
}

/**
 * Replace the rooms skeleton element with rendered rooms HTML and re-init room JS.
 */
function hydrateRooms(container, html) {
    const target = container.querySelector('[data-hotel-section="rooms"]');
    if (!target) return;

    if (!html || html.trim() === "") {
        target.remove();
        return;
    }

    const wrapper = document.createElement("div");
    wrapper.innerHTML = html;
    const fragment = document.createDocumentFragment();
    while (wrapper.firstChild) {
        fragment.appendChild(wrapper.firstChild);
    }
    target.replaceWith(fragment);

    // Re-init Swiper sliders for room photo galleries
    initHotelSingleSlider();
    // Re-execute inline <script> tags (room filter logic)
    executeInlineScripts();
}
